import { Middleware } from '@reduxjs/toolkit';
import {
  trackPhraseAccess,
  trackPhraseReference,
  startEditSession,
  endEditSession,
  trackSessionEdit,
  performCleanup,
  loadPersistedState,
} from '../slices/recentPhrasesSlice';

// Type for the recent phrases state slice to avoid circular dependency
type RecentPhrasesSliceState = {
  phrases: Record<string, any>;
  sortedIds: string[];
  lastCleanup: string;
  settings: any;
  currentSession: {
    phraseId?: string;
    startTime?: number;
    edits: number;
  };
};

type StoreState = {
  recentPhrases: RecentPhrasesSliceState;
  // Other slices not needed for this middleware
  [key: string]: any;
};

const STORAGE_KEY = 'phrasewright:recentPhrases';
const CLEANUP_INTERVAL = 1000 * 60 * 60; // 1 hour
const PERSISTENCE_DEBOUNCE = 1000; // 1 second

let persistenceTimer: NodeJS.Timeout | null = null;
let cleanupTimer: NodeJS.Timeout | null = null;

// Load state from localStorage
export const loadRecentPhrasesFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed;
    }
  } catch (error) {
    console.error('Failed to load recent phrases from storage:', error);
  }
  return null;
};

// Save state to localStorage
const saveRecentPhrasesToStorage = (state: RecentPhrasesSliceState) => {
  try {
    // Don't persist current session
    const { currentSession, ...stateToPersist } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToPersist));
  } catch (error) {
    console.error('Failed to save recent phrases to storage:', error);
  }
};

// Debounced persistence
const debouncedPersist = (state: RecentPhrasesSliceState) => {
  if (persistenceTimer) {
    clearTimeout(persistenceTimer);
  }
  persistenceTimer = setTimeout(() => {
    saveRecentPhrasesToStorage(state);
  }, PERSISTENCE_DEBOUNCE);
};

// Recent phrases tracking middleware
export const recentPhrasesMiddleware: Middleware = (store) => {
  // Helper to check if store is ready and has the recentPhrases reducer
  const isStoreReady = () => {
    try {
      const state = store.getState() as StoreState;
      return state && state.recentPhrases !== undefined;
    } catch (error) {
      console.warn('Store not ready for recentPhrases middleware:', error);
      return false;
    }
  };

  // Load persisted state on initialization - but only after store is ready
  const initializePersistedState = () => {
    if (!isStoreReady()) {
      // Retry after a short delay if store isn't ready
      setTimeout(initializePersistedState, 10);
      return;
    }

    try {
      const persisted = loadRecentPhrasesFromStorage();
      if (persisted) {
        store.dispatch(loadPersistedState(persisted));
      }
    } catch (error) {
      console.error('Failed to load persisted recent phrases state:', error);
    }
  };

  // Initialize with a delay to ensure store is ready
  setTimeout(initializePersistedState, 0);

  // Set up periodic cleanup - also with store readiness check
  cleanupTimer = setInterval(() => {
    if (isStoreReady()) {
      try {
        store.dispatch(performCleanup());
      } catch (error) {
        console.error('Failed to perform periodic cleanup:', error);
      }
    }
  }, CLEANUP_INTERVAL);

  // Clean up on unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (!isStoreReady()) return;

      try {
        const state = store.getState() as StoreState;

        // End any active edit session
        if (state.recentPhrases.currentSession.phraseId) {
          store.dispatch(endEditSession());
        }

        // Persist immediately
        saveRecentPhrasesToStorage(state.recentPhrases);
      } catch (error) {
        console.error('Failed to save state on unload:', error);
      }

      // Clear timers
      if (cleanupTimer) clearInterval(cleanupTimer);
      if (persistenceTimer) clearTimeout(persistenceTimer);
    });
  }

  return (next) => (action) => {
    const result = next(action);

    // Only proceed if store is ready
    if (!isStoreReady()) {
      return result;
    }

    try {
      const state = store.getState() as StoreState;

      // Auto-track based on other actions
      const actionType = (action as any).type;
      switch (actionType) {
        // Track when a file is opened
        case 'file/selectFile':
        case 'file/openFile': {
          const file = (action as any).payload;
          if (file) {
            store.dispatch(
              trackPhraseAccess({
                id: file.id,
                path: file.path,
                title: file.name || file.metadata?.title || 'Untitled',
                type: file.metadata?.type || 'document',
                tags: file.metadata?.tags || [],
                metadata: {
                  author: file.metadata?.author,
                  description: file.metadata?.description,
                },
              })
            );

            // Start edit session
            store.dispatch(startEditSession(file.id));
          }
          break;
        }

        // Track when a file is closed
        case 'file/closeFile': {
          const fileId = (action as any).payload;
          if (state.recentPhrases.currentSession.phraseId === fileId) {
            store.dispatch(endEditSession());
          }
          break;
        }

        // Track edits
        case 'file/updateFileContent':
        case 'file/saveFile': {
          store.dispatch(trackSessionEdit());
          break;
        }

        // Track reference usage
        case 'reference/insertReference': {
          const referenceId = (action as any).payload?.targetId;
          if (referenceId) {
            store.dispatch(trackPhraseReference(referenceId));
          }
          break;
        }

        // Track search result clicks
        case 'search/selectResult': {
          const result = (action as any).payload;
          if (result?.id) {
            store.dispatch(
              trackPhraseAccess({
                id: result.id,
                path: result.path,
                title: result.title || 'Untitled',
                type: result.type || 'document',
                tags: result.tags || [],
              })
            );
          }
          break;
        }
      }

      // Persist state changes for recent phrases actions
      if (actionType.startsWith('recentPhrases/')) {
        debouncedPersist(state.recentPhrases);
      }
    } catch (error) {
      console.error('Error in recentPhrasesMiddleware:', error);
    }

    return result;
  };
};

// Export helper to get recent phrases for autocomplete
export const getRecentPhrasesForAutocomplete = (
  state: { recentPhrases: RecentPhrasesSliceState } | StoreState,
  query: string = '',
  limit: number = 10
): Array<{
  id: string;
  path: string;
  title: string;
  score: number;
  type: 'document' | 'phrase';
}> => {
  const { phrases, sortedIds } = state.recentPhrases;
  const lowerQuery = query.toLowerCase();

  return sortedIds
    .filter((id) => {
      const phrase = phrases[id];
      return (
        phrase.title.toLowerCase().includes(lowerQuery) ||
        phrase.tags.some((tag: string) =>
          tag.toLowerCase().includes(lowerQuery)
        )
      );
    })
    .slice(0, limit)
    .map((id) => {
      const phrase = phrases[id];
      return {
        id: phrase.id,
        path: phrase.path,
        title: phrase.title,
        score: phrase.score,
        type: phrase.type,
      };
    });
};
